import { useCallback, useRef, useState } from 'react';

import { ColumnSizingInfoState, ColumnSizingState } from '@tanstack/react-table';

import { getFromLocalStorage, setToLocalStorage } from '@/utils/localStorage';

const COLUMN_SIZING_STORAGE_PREFIX = 'table-column-sizing';

/**
 * Manages column sizing state with localStorage persistence and throttled updates.
 * Use this when `useDataTable` cannot be used (e.g., tables with custom state management).
 */
export function useColumnSizing(tableId: string) {
  const storageKey = `${COLUMN_SIZING_STORAGE_PREFIX}-${tableId}`;

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    () => getFromLocalStorage<ColumnSizingState>(storageKey) ?? {}
  );
  const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>(
    {} as ColumnSizingInfoState
  );

  const rafRef = useRef<number | null>(null);

  const throttledSetColumnSizing = useCallback(
    (updater: ColumnSizingState | ((old: ColumnSizingState) => ColumnSizingState)) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setColumnSizing(updater);
      });
    },
    []
  );

  const saveColumnSizing = useCallback(() => {
    setColumnSizing(current => {
      setToLocalStorage(storageKey, current);
      return current;
    });
  }, [storageKey]);

  return {
    columnSizing,
    columnSizingInfo,
    onColumnSizingChange: throttledSetColumnSizing,
    onColumnSizingInfoChange: setColumnSizingInfo,
    saveColumnSizing,
  };
}
